<template>
  <v-container fluid class="pt-0">

    <v-card v-if="loading" class="text-center pa-6">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <p class="mt-4">Loading organization...</p>
    </v-card>

    <v-alert v-if="error" type="error" prominent class="mb-4">{{ error }}</v-alert>

    <template v-if="org && !loading">

      <!-- Profile -->
      <v-card class="mb-4">
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2">fas fa-sitemap</v-icon>
          {{ org.display_name }}
          <v-spacer></v-spacer>
          <v-chip :color="org.account_status?.toLowerCase() === 'active' ? 'green' : 'orange'">
            {{ org.account_status }}
          </v-chip>
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text v-if="!editingProfile">
          <v-list density="compact">
            <v-list-item>
              <v-list-item-title>Display Name</v-list-item-title>
              <v-list-item-subtitle>{{ org.display_name }}</v-list-item-subtitle>
            </v-list-item>
            <v-list-item v-if="org.logo_url">
              <v-list-item-title>Logo URL</v-list-item-title>
              <v-list-item-subtitle>{{ org.logo_url }}</v-list-item-subtitle>
            </v-list-item>
            <v-list-item v-if="org.page_background_color">
              <v-list-item-title>Login Background</v-list-item-title>
              <v-list-item-subtitle>
                <span
                  :style="{ background: org.page_background_color, width: '16px', height: '16px', borderRadius: '3px', display: 'inline-block', marginRight: '6px' }"
                ></span>
                {{ org.page_background_color }}
              </v-list-item-subtitle>
            </v-list-item>
            <v-list-item v-if="org.primary_color">
              <v-list-item-title>Brand Color</v-list-item-title>
              <v-list-item-subtitle class="d-flex align-center ga-2">
                <span
                  :style="{ background: org.primary_color, width: '16px', height: '16px', borderRadius: '3px', display: 'inline-block', marginRight: '6px' }"
                ></span>
                {{ org.primary_color }}
              </v-list-item-subtitle>
            </v-list-item>
          </v-list>
        </v-card-text>

        <v-card-text v-else>
          <v-text-field
            v-model="profileForm.display_name"
            label="Display Name"
            variant="outlined"
            density="compact"
            class="mb-2"
            :rules="displayNameRules"
          ></v-text-field>
          <v-text-field
            v-model="profileForm.logo_url"
            label="Logo URL"
            variant="outlined"
            density="compact"
            class="mb-2"
            :rules="logoUrlRules"
          ></v-text-field>
          <v-text-field
            v-model="profileForm.primary_color"
            label="Brand Color (hex)"
            variant="outlined"
            density="compact"
            placeholder="#3297e3"
            class="mb-2"
            :rules="hexColorRules"
          >
            <template #append-inner>
              <v-menu :close-on-content-click="false">
                <template #activator="{ props }">
                  <div
                    v-bind="props"
                    class="color-swatch"
                    :style="{ background: profileForm.primary_color || '#3297e3' }"
                  ></div>
                </template>
                <v-color-picker
                  :model-value="profileForm.primary_color"
                  @update:model-value="val => profileForm.primary_color = toHex(val)"
                  mode="hex"
                  hide-inputs
                />
              </v-menu>
            </template>
          </v-text-field>
          <v-text-field
            v-model="profileForm.page_background_color"
            label="Login Page Background Color (hex)"
            variant="outlined"
            density="compact"
            placeholder="#323436"
            :rules="hexColorRules"
          >
            <template #append-inner>
              <v-menu :close-on-content-click="false">
                <template #activator="{ props }">
                  <div
                    v-bind="props"
                    class="color-swatch"
                    :style="{ background: profileForm.page_background_color || '#323436' }"
                  ></div>
                </template>
                <v-color-picker
                  :model-value="profileForm.page_background_color"
                  @update:model-value="val => profileForm.page_background_color = toHex(val)"
                  mode="hex"
                  hide-inputs
                />
              </v-menu>
            </template>
          </v-text-field>
        </v-card-text>

        <v-card-actions v-if="isOwnerOrAdmin">
          <v-spacer></v-spacer>
          <template v-if="!editingProfile">
            <v-btn variant="text" @click="startEditProfile">Edit</v-btn>
          </template>
          <template v-else>
            <v-btn variant="text" @click="cancelEditProfile">Cancel</v-btn>
            <v-btn color="primary" variant="elevated" :loading="savingProfile" :disabled="!isProfileFormValid" @click="saveProfile">Save</v-btn>
          </template>
        </v-card-actions>
      </v-card>

      <!-- Members -->
      <v-card class="mb-4" v-if="isOwnerOrAdmin">
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2">fas fa-users</v-icon>
          Members
          <v-spacer></v-spacer>
          <v-btn color="primary" size="small" prepend-icon="fas fa-envelope" @click="showInviteDialog = true">
            Invite
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>

        <v-card v-if="membersLoading" class="text-center pa-4" flat>
          <v-progress-circular indeterminate color="primary" size="24"></v-progress-circular>
        </v-card>

        <v-table v-else density="compact">
          <thead>
            <tr>
              <th>Name / Email</th>
              <th>Roles</th>
              <th>Joined</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="member in members" :key="member.user_auth0_id">
              <td>
                <div class="font-weight-medium">{{ member.name || '—' }}</div>
                <div class="text-caption text-medium-emphasis">{{ member.email }}</div>
              </td>
              <td>
                <v-chip
                  v-for="role in (member.roles ?? [])"
                  :key="role"
                  size="x-small"
                  class="mr-1"
                  :color="roleColor(role)"
                >{{ roleLabel(role) }}</v-chip>
                <span v-if="!member.roles?.length" class="text-medium-emphasis text-caption">No role</span>
              </td>
              <td class="text-caption text-medium-emphasis">{{ formatDate(member.joined_on) }}</td>
              <td class="text-right">
                <v-btn
                  icon
                  size="x-small"
                  variant="text"
                  @click="openEditRoles(member)"
                  title="Edit roles"
                  :disabled="member.roles?.includes('org-owner')"
                ><v-icon size="14">fas fa-pen</v-icon></v-btn>
                <v-btn
                  v-if="orgRoles.includes('org-owner') && !member.roles?.includes('org-owner')"
                  icon
                  size="x-small"
                  variant="text"
                  color="warning"
                  @click="confirmTransferOwnership(member)"
                  title="Transfer ownership"
                ><v-icon size="14">fas fa-crown</v-icon></v-btn>
                <v-btn
                  icon
                  size="x-small"
                  variant="text"
                  color="error"
                  @click="confirmRemoveMember(member)"
                  title="Remove member"
                  :disabled="member.roles?.includes('org-owner')"
                ><v-icon size="14">fas fa-trash</v-icon></v-btn>
              </td>
            </tr>
          </tbody>
        </v-table>
      </v-card>

      <!-- Pending Invitations -->
      <v-card v-if="isOwnerOrAdmin && invitations.length">
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2">fas fa-paper-plane</v-icon>
          Pending Invitations
        </v-card-title>
        <v-divider></v-divider>
        <v-table density="compact">
          <thead>
            <tr>
              <th>Email</th>
              <th>Roles</th>
              <th>Expires</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="inv in invitations" :key="inv.id">
              <td>{{ inv.invitee.email }}</td>
              <td>
                <v-chip
                  v-for="role in inv.roles"
                  :key="typeof role === 'object' ? role.name : role"
                  size="x-small"
                  class="mr-1"
                  :color="roleColor(role)"
                >{{ roleLabel(role) }}</v-chip>
              </td>
              <td class="text-caption text-medium-emphasis">{{ formatDate(inv.expires_at) }}</td>
              <td class="text-right">
                <v-btn icon size="x-small" variant="text" color="error" @click="revokeInvitation(inv.id)" title="Revoke">
                  <v-icon size="14">fas fa-times</v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
        </v-table>
      </v-card>

    </template>

    <!-- Invite dialog -->
    <v-dialog v-model="showInviteDialog" max-width="480">
      <v-card>
        <v-card-title>Invite Member</v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-text-field
            v-model="inviteForm.email"
            label="Email address"
            type="email"
            variant="outlined"
            density="compact"
            class="mb-3"
            :rules="emailRules"
          ></v-text-field>
          <v-select
            v-model="inviteForm.roles"
            :items="assignableRoles"
            label="Role"
            variant="outlined"
            density="compact"
            multiple
            chips
            :rules="[v => (v && v.length > 0) || 'At least one role is required']"
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showInviteDialog = false">Cancel</v-btn>
          <v-btn color="primary" variant="elevated" :loading="inviting" :disabled="!isInviteFormValid" @click="sendInvite">Send Invite</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit roles dialog -->
    <v-dialog v-model="showEditRolesDialog" max-width="480">
      <v-card>
        <v-card-title>Edit Roles — {{ editingMember?.name || editingMember?.email }}</v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-select
            v-model="editRolesForm"
            :items="assignableRoles"
            label="Roles"
            variant="outlined"
            density="compact"
            multiple
            chips
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showEditRolesDialog = false">Cancel</v-btn>
          <v-btn color="primary" variant="elevated" :loading="savingRoles" @click="saveRoles">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Remove member confirmation -->
    <v-dialog v-model="showRemoveDialog" max-width="420">
      <v-card>
        <v-card-title>Remove Member</v-card-title>
        <v-card-text>
          Remove <strong>{{ removingMember?.name || removingMember?.email }}</strong> from the organization?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showRemoveDialog = false">Cancel</v-btn>
          <v-btn color="error" variant="elevated" :loading="removingLoading" @click="removeMember">Remove</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Transfer ownership confirmation -->
    <v-dialog v-model="showTransferDialog" max-width="480">
      <v-card>
        <v-card-title>Transfer Ownership</v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <p>Transfer ownership to <strong>{{ transferTarget?.name || transferTarget?.email }}</strong>?</p>
          <p class="mt-2 text-medium-emphasis text-body-2">You will be demoted to Admin. This cannot be undone without the new owner's involvement.</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showTransferDialog = false">Cancel</v-btn>
          <v-btn color="warning" variant="elevated" :loading="transferLoading" @click="transferOwnership">Transfer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000">
      {{ snackbar.message }}
    </v-snackbar>

  </v-container>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useAuth0 } from '@auth0/auth0-vue';
import axios from 'axios';

const BASE = `${process.env.VUE_APP_CBE_BASE_URL}/piccolo/organizations`;

const ROLE_META = {
  'org-owner':      { label: 'Owner',      color: 'amber-darken-2' },
  'org-admin':      { label: 'Admin',      color: 'indigo' },
  'org-developer':  { label: 'Developer',  color: 'blue' },
  'org-accountant': { label: 'Accountant', color: 'teal' },
  'org-marketer':   { label: 'Marketer',   color: 'cyan' },
};

const ASSIGNABLE_ROLES = Object.entries(ROLE_META)
  .filter(([role]) => role !== 'org-owner')
  .map(([value, { label }]) => ({ title: label, value }));

export default {
  name: 'OrgView',
  setup() {
    const store = useStore();
    const { getAccessTokenSilently, loginWithRedirect, user: auth0User } = useAuth0();

    const org          = ref(null);
    const members      = ref([]);
    const invitations  = ref([]);
    const loading      = ref(true);
    const membersLoading = ref(false);
    const error        = ref(null);

    const editingProfile = ref(false);
    const savingProfile  = ref(false);
    const profileForm    = ref({ display_name: '', logo_url: '', primary_color: '', page_background_color: '' });

    const showInviteDialog = ref(false);
    const inviting         = ref(false);
    const inviteForm       = ref({ email: '', roles: [] });

    const showEditRolesDialog = ref(false);
    const editingMember       = ref(null);
    const editRolesForm       = ref([]);
    const savingRoles         = ref(false);

    const showRemoveDialog  = ref(false);
    const removingMember    = ref(null);
    const removingLoading   = ref(false);

    const showTransferDialog = ref(false);
    const transferTarget     = ref(null);
    const transferLoading    = ref(false);

    const snackbar = ref({ show: false, message: '', color: 'success' });

    const orgRoles = computed(() => store.getters.orgRoles);
    const isOwnerOrAdmin = computed(() =>
      orgRoles.value.some(r => r === 'org-owner' || r === 'org-admin')
    );
    const assignableRoles = ASSIGNABLE_ROLES;

    const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

    function isValidUrl(v) {
      if (!v) return true;
      try {
        const url = new URL(v);
        return ['http:', 'https:'].includes(url.protocol);
      } catch {
        return false;
      }
    }

    const displayNameRules = [
      v => !!v || 'Display Name is required',
      v => (v && v.trim().length >= 2) || 'Must be at least 2 characters',
    ];
    const logoUrlRules = [
      v => isValidUrl(v) || 'Must be a valid URL (starting with http:// or https://)',
    ];
    const hexColorRules = [
      v => !v || HEX_RE.test(v) || 'Must be a valid 6-digit hex color (e.g. #3297e3)',
    ];
    const emailRules = [
      v => !!v || 'Email is required',
      v => /.+@.+\..+/.test(v) || 'Must be a valid email address',
    ];

    const isProfileFormValid = computed(() => {
      const { display_name, logo_url, primary_color, page_background_color } = profileForm.value;
      return (
        displayNameRules.every(r => r(display_name) === true) &&
        logoUrlRules.every(r => r(logo_url) === true) &&
        hexColorRules.every(r => r(primary_color) === true) &&
        hexColorRules.every(r => r(page_background_color) === true)
      );
    });

    const isInviteFormValid = computed(() => {
      const { email, roles } = inviteForm.value;
      return emailRules.every(r => r(email) === true) && roles.length > 0;
    });

    async function authHeaders() {
      const token = await getAccessTokenSilently();
      return { Authorization: `Bearer ${token}` };
    }

    function toHex(val) {
      if (!val) return '';
      // v-color-picker may return #RRGGBBAA — strip alpha
      return typeof val === 'string' ? val.slice(0, 7) : val.hex?.slice(0, 7) ?? '';
    }

    function notify(message, color = 'success') {
      snackbar.value = { show: true, message, color };
    }

    async function fetchOrg(headers) {
      try {
        const { data } = await axios.get(`${BASE}/me`, { headers });
        org.value = data;
        store.commit('SET_ORG_BRANDING', {
          logoUrl: data.logo_url || null,
          primaryColor: data.primary_color || null,
          pageBackgroundColor: data.page_background_color || null,
        });
      } catch (e) {
        error.value = 'Failed to load organization.';
      } finally {
        loading.value = false;
      }
    }

    async function fetchMembers(headers) {
      if (!isOwnerOrAdmin.value) return;
      membersLoading.value = true;
      try {
        const [membersRes, invitesRes] = await Promise.all([
          axios.get(`${BASE}/me/members`,     { headers }),
          axios.get(`${BASE}/me/invitations`, { headers }),
        ]);
        members.value = (membersRes.data || []).map(m => ({
          ...m,
          roles: (m.roles ?? []).map(r => typeof r === 'object' ? (r?.name ?? '') : r),
        }));
        invitations.value = invitesRes.data || [];
      } catch {
        notify('Failed to load members.', 'error');
      } finally {
        membersLoading.value = false;
      }
    }

    function startEditProfile() {
      profileForm.value = {
        display_name:  org.value.display_name,
        logo_url:               org.value.logo_url || '',
        primary_color:          org.value.primary_color || '',
        page_background_color:  org.value.page_background_color || '',
      };
      editingProfile.value = true;
    }

    function cancelEditProfile() { editingProfile.value = false; }

    async function saveProfile() {
      savingProfile.value = true;
      try {
        const payload = {};
        payload.display_name = profileForm.value.display_name;
        payload.logo_url              = profileForm.value.logo_url || null;
        payload.primary_color         = profileForm.value.primary_color || null;
        payload.page_background_color = profileForm.value.page_background_color || null;
        const { data } = await axios.patch(`${BASE}/me`, payload, { headers: await authHeaders() });
        org.value = data;
        store.commit('SET_ORG_BRANDING', {
          logoUrl: data.logo_url || null,
          primaryColor: data.primary_color || null,
          pageBackgroundColor: data.page_background_color || null,
        });
        editingProfile.value = false;
        notify('Organization updated.');
      } catch {
        notify('Failed to save changes.', 'error');
      } finally {
        savingProfile.value = false;
      }
    }

    async function sendInvite() {
      inviting.value = true;
      try {
        const { data } = await axios.post(
          `${BASE}/me/invitations`,
          inviteForm.value,
          { headers: await authHeaders() }
        );
        invitations.value.push(data);
        showInviteDialog.value = false;
        inviteForm.value = { email: '', roles: [] };
        notify(`Invitation sent to ${data.invitee.email}.`);
      } catch {
        notify('Failed to send invitation.', 'error');
      } finally {
        inviting.value = false;
      }
    }

    async function revokeInvitation(id) {
      try {
        await axios.delete(`${BASE}/me/invitations/${id}`, { headers: await authHeaders() });
        invitations.value = invitations.value.filter(i => i.id !== id);
        notify('Invitation revoked.');
      } catch {
        notify('Failed to revoke invitation.', 'error');
      }
    }

    function openEditRoles(member) {
      editingMember.value  = member;
      editRolesForm.value  = [...(member.roles ?? [])];
      showEditRolesDialog.value = true;
    }

    async function saveRoles() {
      savingRoles.value = true;
      try {
        await axios.put(
          `${BASE}/me/members/${editingMember.value.user_auth0_id}/roles`,
          { roles: editRolesForm.value },
          { headers: await authHeaders() }
        );
        editingMember.value.roles = [...editRolesForm.value];
        showEditRolesDialog.value = false;
        notify('Roles updated.');
      } catch {
        notify('Failed to update roles.', 'error');
      } finally {
        savingRoles.value = false;
      }
    }

    function confirmRemoveMember(member) {
      removingMember.value  = member;
      showRemoveDialog.value = true;
    }

    async function removeMember() {
      removingLoading.value = true;
      try {
        await axios.delete(
          `${BASE}/me/members/${removingMember.value.user_auth0_id}`,
          { headers: await authHeaders() }
        );
        members.value = members.value.filter(m => m.user_auth0_id !== removingMember.value.user_auth0_id);
        showRemoveDialog.value = false;
        notify('Member removed.');
      } catch {
        notify('Failed to remove member.', 'error');
      } finally {
        removingLoading.value = false;
      }
    }

    function confirmTransferOwnership(member) {
      transferTarget.value    = member;
      showTransferDialog.value = true;
    }

    async function transferOwnership() {
      if (!transferTarget.value) return;
      transferLoading.value = true;
      try {
        const headers = await authHeaders();
        await axios.post(
          `${BASE}/me/transfer-ownership`,
          { new_owner_auth0_id: transferTarget.value.user_auth0_id },
          { headers }
        );
        const updatedRoles = orgRoles.value.map(r => r === 'org-owner' ? 'org-admin' : r);
        store.commit('SET_ORG', {
          isOrgMember: true,
          orgId: store.state.org.orgId,
          orgRoles: updatedRoles,
        });
        const currentSub = auth0User.value?.sub;
        if (currentSub) {
          const me = members.value.find(m => m.user_auth0_id === currentSub);
          if (me) me.roles = updatedRoles.filter(r => ['org-owner','org-admin','org-developer','org-accountant','org-marketer'].includes(r));
        }
        if (transferTarget.value?.user_auth0_id) {
          const target = members.value.find(m => m.user_auth0_id === transferTarget.value.user_auth0_id);
          if (target) target.roles = ['org-owner'];
        }
        showTransferDialog.value = false;
        notify('Ownership transferred. You have been demoted to Admin.');
        try {
          await getAccessTokenSilently({ ignoreCache: true });
        } catch {
          loginWithRedirect({ appState: { target: '/console/organization' } });
          return;
        }
        await fetchMembers(await authHeaders());
      } catch {
        notify('Failed to transfer ownership.', 'error');
      } finally {
        transferLoading.value = false;
      }
    }

    function roleLabel(role) {
      const rName = typeof role === 'object' ? (role?.name ?? '') : role;
      return ROLE_META[rName]?.label ?? rName;
    }
    function roleColor(role) {
      const rName = typeof role === 'object' ? (role?.name ?? '') : role;
      return ROLE_META[rName]?.color ?? 'grey';
    }


    function formatDate(iso) {
      if (!iso) return '—';
      const date = new Date(iso);
      return isNaN(date.getTime()) ? '—' : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    }

    onMounted(async () => {
      try {
        const headers = await authHeaders();
        await fetchOrg(headers);
        await fetchMembers(headers);
      } catch {
        error.value = 'Failed to load organization.';
        loading.value = false;
      }
    });

    return {
      org, members, invitations, loading, membersLoading, error,
      editingProfile, savingProfile, profileForm,
      showInviteDialog, inviting, inviteForm,
      showEditRolesDialog, editingMember, editRolesForm, savingRoles,
      showRemoveDialog, removingMember, removingLoading,
      snackbar, isOwnerOrAdmin, orgRoles, assignableRoles,
      displayNameRules, logoUrlRules, hexColorRules, emailRules,
      isProfileFormValid, isInviteFormValid,
      startEditProfile, cancelEditProfile, saveProfile,
      sendInvite, revokeInvitation,
      openEditRoles, saveRoles,
      confirmRemoveMember, removeMember,
      showTransferDialog, transferTarget, transferLoading, confirmTransferOwnership, transferOwnership,
      roleLabel, roleColor, formatDate, toHex,
    };
  },
};
</script>

<style scoped>
.color-swatch {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  cursor: pointer;
  flex-shrink: 0;
}
</style>
